import React, { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const cityCenters = {
  Toronto: [43.6532, -79.3832],
  Brampton: [43.7315, -79.7624],
  Mississauga: [43.589, -79.6441],
  Ottawa: [45.4215, -75.6972],
  "North York": [43.7615, -79.4111],
  Scarborough: [43.7764, -79.2318],
  Markham: [43.8561, -79.337],
  Vaughan: [43.8563, -79.5085],
  Hamilton: [43.2557, -79.8711],
  London: [42.9849, -81.2453],
  Waterloo: [43.4643, -80.5204],
  Kitchener: [43.4516, -80.4925],
  Kingston: [44.2312, -76.486],
  Windsor: [42.3149, -83.0364],
  "Richmond Hill": [43.8828, -79.4403],
};

function getCoords(doctor, index) {
  const lat = Number(doctor.latitude);
  const lng = Number(doctor.longitude);

  const validLat =
    !Number.isNaN(lat) &&
    lat !== 0 &&
    lat >= 41 &&
    lat <= 57;

  const validLng =
    !Number.isNaN(lng) &&
    lng !== 0 &&
    lng >= -96 &&
    lng <= -52;

  if (validLat && validLng) {
    return [lat, lng, false];
  }

  const cityCoord = cityCenters[doctor.city];

  if (cityCoord) {
    const offset = (index % 8) * 0.006;
    return [cityCoord[0] + offset, cityCoord[1] + offset, true];
  }

  return [43.6532, -79.3832, true];
}

function AutoFitMap({ mappedDoctors }) {
  const map = useMap();

  useEffect(() => {
    if (!mappedDoctors || mappedDoctors.length === 0) return;

    const points = mappedDoctors.map((doctor) => [
      doctor.mapLat,
      doctor.mapLng,
    ]);

    if (points.length === 1) {
      map.setView(points[0], 12, { animate: true });
      return;
    }

    const bounds = L.latLngBounds(points);

    map.fitBounds(bounds, {
      padding: [40, 40],
      maxZoom: 12,
      animate: true,
    });
  }, [mappedDoctors, map]);

  return null;
}

function hasActiveFilter() {
  const params = new URLSearchParams(window.location.search);

  return (
    params.has("city") ||
    params.has("status") ||
    params.has("specialty")
  );
}

function scrollToExactDoctorCard(doctor) {
  if (!hasActiveFilter()) {
    return;
  }

  const doctorName = String(doctor.name || "").toLowerCase().trim();
  const clinicName = String(doctor.clinic_name || "").toLowerCase().trim();

  const mapWrapper = document.querySelector("[data-provider-map='true']");
  const mapBottom = mapWrapper
    ? mapWrapper.getBoundingClientRect().bottom + window.scrollY
    : 0;

  const allElements = Array.from(
    document.querySelectorAll("div, article, section")
  );

  const possibleCards = allElements.filter((element) => {
    const text = String(element.textContent || "").toLowerCase();
    const rect = element.getBoundingClientRect();
    const top = rect.top + window.scrollY;

    const isBelowMap = top > mapBottom - 20;
    const hasDoctorName = doctorName && text.includes(doctorName);
    const hasClinicName = clinicName && text.includes(clinicName);
    const looksLikeCard =
      text.includes("last verified") ||
      text.includes("confidence") ||
      text.includes("call clinic") ||
      text.includes("view details");

    const notMap =
      !element.closest(".leaflet-container") &&
      !element.className?.toString().includes("leaflet");

    return (
      isBelowMap &&
      notMap &&
      (hasDoctorName || hasClinicName) &&
      looksLikeCard &&
      rect.width > 220 &&
      rect.height > 180
    );
  });

  let matchedCard = null;

  if (possibleCards.length > 0) {
    matchedCard = possibleCards.sort((a, b) => {
      const areaA =
        a.getBoundingClientRect().width * a.getBoundingClientRect().height;
      const areaB =
        b.getBoundingClientRect().width * b.getBoundingClientRect().height;

      return areaA - areaB;
    })[0];
  }

  if (!matchedCard) return;

  matchedCard.scrollIntoView({
    behavior: "smooth",
    block: "center",
  });

  matchedCard.classList.add(
    "ring-4",
    "ring-blue-300",
    "ring-offset-4",
    "shadow-2xl",
    "scale-[1.02]",
    "transition-all",
    "duration-300"
  );

  setTimeout(() => {
    matchedCard.classList.remove(
      "ring-4",
      "ring-blue-300",
      "ring-offset-4",
      "shadow-2xl",
      "scale-[1.02]",
      "transition-all",
      "duration-300"
    );
  }, 2400);
}

export default function ProviderMap({ doctors = [] }) {
  const mappedDoctors = doctors.map((doctor, index) => {
    const [lat, lng, estimated] = getCoords(doctor, index);

    return {
      ...doctor,
      mapLat: lat,
      mapLng: lng,
      estimated,
    };
  });

  const center =
    mappedDoctors.length > 0
      ? [mappedDoctors[0].mapLat, mappedDoctors[0].mapLng]
      : [43.6532, -79.3832];

  const canJumpToCard = hasActiveFilter();

  return (
    <div
      data-provider-map="true"
      className="mb-8 rounded-[2rem] overflow-hidden border border-slate-200 bg-white shadow-lg"
    >
      <div className="p-5 border-b border-slate-100">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Map View</h2>

            <p className="text-sm text-slate-500 mt-1">
              Tap a marker to jump to that provider’s card below.
            </p>
          </div>

          <div className="text-xs font-semibold text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-4 py-2">
            {mappedDoctors.length} mapped location
            {mappedDoctors.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      <div className="h-[420px] w-full">
        <MapContainer
          center={center}
          zoom={10}
          scrollWheelZoom={false}
          className="h-full w-full z-0"
        >
          <AutoFitMap mappedDoctors={mappedDoctors} />

          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {mappedDoctors.map((doctor) => (
            <Marker
              key={doctor.id}
              position={[doctor.mapLat, doctor.mapLng]}
            >
              <Popup>
                <div className="min-w-[190px] max-w-[220px] text-center">
                  <h3 className="font-bold text-sm text-slate-900 leading-snug">
                    {doctor.name || doctor.clinic_name}
                  </h3>

                  <p className="text-xs text-slate-500 mt-1">
                    {doctor.city || "Ontario"}
                  </p>

                  {doctor.estimated && (
                    <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
                      Approximate city location
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={() => scrollToExactDoctorCard(doctor)}
                    disabled={!canJumpToCard}
                    className={`mt-3 w-full rounded-xl px-4 py-3 text-sm font-bold shadow ${
                      canJumpToCard
                        ? "bg-[#356DFF] text-white hover:bg-[#255AE6]"
                        : "bg-slate-100 text-slate-400 cursor-not-allowed"
                    }`}
                  >
                    {canJumpToCard
                      ? "View Doctor Card"
                      : "Select a filter first"}
                  </button>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}